/**
 * Color Family Mapping Configuration
 * 
 * Maps color synonyms to their parent color families.
 * Used for semantic color search expansion.
 */

export interface ColorFamily {
  family: string;
  synonyms: string[];
  includesNeutral?: boolean;
}

/**
 * Color families with their synonyms
 * When a user searches for a synonym, we also search for the family color
 */
export const COLOR_FAMILIES: ColorFamily[] = [
  {
    family: 'red',
    synonyms: ['burgundy', 'maroon', 'rust', 'crimson', 'wine', 'scarlet', 'cherry', 'ruby', 'vermilion', 'carmine'],
  },
  {
    family: 'blue',
    synonyms: ['navy', 'azure', 'cobalt', 'sky', 'denim', 'sapphire', 'indigo', 'cerulean', 'teal', 'turquoise', 'aqua'],
  },
  {
    family: 'green',
    synonyms: ['lime', 'olive', 'forest', 'emerald', 'mint', 'sage', 'jade', 'chartreuse', 'hunter', 'moss'],
  },
  {
    family: 'brown',
    synonyms: ['beige', 'tan', 'cream', 'khaki', 'sand', 'camel', 'chocolate', 'coffee', 'mocha', 'taupe', 'sepia'],
    includesNeutral: true,
  },
  {
    family: 'yellow',
    synonyms: ['gold', 'golden', 'mustard', 'lemon', 'canary', 'amber', 'honey', 'saffron', 'blonde'],
  },
  {
    family: 'orange',
    synonyms: ['tangerine', 'peach', 'coral', 'apricot', 'pumpkin', 'copper', 'terracotta'],
  },
  {
    family: 'purple',
    synonyms: ['violet', 'lavender', 'plum', 'grape', 'magenta', 'mauve', 'lilac', 'amethyst', 'eggplant'],
  },
  {
    family: 'pink',
    synonyms: ['rose', 'blush', 'salmon', 'fuchsia', 'hot pink', 'bubblegum', 'carnation'],
  },
  {
    family: 'black',
    synonyms: ['ebony', 'charcoal', 'onyx', 'jet', 'obsidian', 'noir', 'raven'],
  },
  {
    family: 'white',
    synonyms: ['ivory', 'pearl', 'snow', 'cream', 'off-white', 'eggshell', 'alabaster'],
  },
  {
    family: 'gray',
    synonyms: ['grey', 'silver', 'slate', 'ash', 'charcoal', 'pewter', 'stone', 'graphite'],
  },
];

/**
 * Build a reverse lookup map: synonym -> family
 */
export const SYNONYM_TO_FAMILY: Record<string, string[]> = {};

// Initialize the reverse lookup
COLOR_FAMILIES.forEach(cf => {
  // The family itself maps to itself
  if (!SYNONYM_TO_FAMILY[cf.family]) {
    SYNONYM_TO_FAMILY[cf.family] = [cf.family];
  }
  
  // Each synonym maps to the family
  cf.synonyms.forEach(synonym => {
    if (!SYNONYM_TO_FAMILY[synonym]) {
      SYNONYM_TO_FAMILY[synonym] = [];
    }
    if (!SYNONYM_TO_FAMILY[synonym].includes(cf.family)) {
      SYNONYM_TO_FAMILY[synonym].push(cf.family);
    }
  });
  
  // If includes neutral, add that mapping too
  if (cf.includesNeutral) {
    cf.synonyms.forEach(synonym => {
      if (!SYNONYM_TO_FAMILY[synonym].includes('neutral')) {
        SYNONYM_TO_FAMILY[synonym].push('neutral');
      }
    });
  }
});

/**
 * Get all color words (families + synonyms) for detection
 */
export const ALL_COLOR_WORDS: string[] = [
  ...COLOR_FAMILIES.map(cf => cf.family),
  ...COLOR_FAMILIES.flatMap(cf => cf.synonyms),
].filter((v, i, a) => a.indexOf(v) === i); // unique

/**
 * Expand a color word to its family (for search)
 * e.g., "burgundy" -> ["burgundy", "red"]
 */
export function expandColorToFamily(color: string): string[] {
  const colorLower = color.toLowerCase();
  const families = SYNONYM_TO_FAMILY[colorLower];
  
  if (families && families.length > 0) {
    // Return the original + the family colors
    return [colorLower, ...families].filter((v, i, a) => a.indexOf(v) === i);
  }
  
  return [colorLower];
}

/**
 * Expand a query by adding color family terms
 * e.g., "burgundy shirt" -> ["burgundy shirt", "red shirt"]
 */
export function expandQueryWithColorFamilies(query: string): string[] {
  const queryLower = query.toLowerCase();
  const expandedQueries = [query];
  
  // Find color words in the query
  for (const colorWord of ALL_COLOR_WORDS) {
    if (queryLower.includes(colorWord)) {
      const families = SYNONYM_TO_FAMILY[colorWord];
      if (families) {
        families.forEach(family => {
          if (family !== colorWord) {
            // Create a new query with the family color instead
            const newQuery = query.replace(new RegExp(colorWord, 'gi'), family);
            if (!expandedQueries.includes(newQuery)) {
              expandedQueries.push(newQuery);
            }
          }
        });
      }
    }
  }
  
  return expandedQueries;
}

/**
 * Get the color family for a given color
 * e.g., "burgundy" -> "red"
 */
export function getColorFamily(color: string): string | null {
  const colorLower = color.toLowerCase();
  const families = SYNONYM_TO_FAMILY[colorLower];
  
  if (families && families.length > 0) {
    // Return the first family (primary)
    return families[0];
  }
  
  return null;
}

/**
 * Check if two colors are in the same family
 */
export function colorsInSameFamily(color1: string, color2: string): boolean {
  const family1 = getColorFamily(color1);
  const family2 = getColorFamily(color2);
  
  if (!family1 || !family2) return false;
  return family1 === family2;
}

/**
 * Get color family info for enrichment
 * Returns the family and synonyms for a given color
 */
export function getColorFamilyInfo(color: string): { color_family: string; color_synonyms: string[] } | null {
  const colorLower = color.toLowerCase();
  
  // Find the family this color belongs to
  for (const cf of COLOR_FAMILIES) {
    if (cf.family === colorLower || cf.synonyms.includes(colorLower)) {
      return {
        color_family: cf.family,
        color_synonyms: cf.synonyms,
      };
    }
  }
  
  return null;
}

